import React, { useState } from 'react';
import { addMonths, parseISO } from 'date-fns';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { ReceivedUseOfForceAuthorizationRequestModel } from '../../../../api-service/clients';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { formatDate } from '../../../../utilities';
import { authorizationDecisionMakerLabels, authorizationTypeShortLabels } from '../../serviceprovider/use-of-force/use-of-force-labels';
import { UseOfForceAuthorizationDecision } from './use-of-force-authorization-api';

export type UseOfForceAuthorizationDecisionSubject = Pick<
  ReceivedUseOfForceAuthorizationRequestModel,
  'authorizationType' | 'requestedPeriodFrom' | 'requestedPeriodTo' | 'serviceProviderUnitName' | 'maxPeriodMonths' | 'decisionMaker'
>;

interface UseOfForceAuthorizationDecisionDialogProps {
  request: UseOfForceAuthorizationDecisionSubject;
  // The leader records what another kommune decided rather than deciding.
  isFromAnotherKommune?: boolean;
  onSave: (decision: UseOfForceAuthorizationDecision) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceAuthorizationDecisionDialog(props: UseOfForceAuthorizationDecisionDialogProps) {
  const { request, isFromAnotherKommune, onSave, onClose } = props;
  const [granted, setGranted] = useState<boolean>();
  const [periodFrom, setPeriodFrom] = useState(asPlainDate(request.requestedPeriodFrom));
  const [periodTo, setPeriodTo] = useState(asPlainDate(request.requestedPeriodTo));
  const [isSaving, setIsSaving] = useState(false);

  const latestPeriodTo = periodFrom && request.maxPeriodMonths ? addMonths(parseISO(periodFrom), request.maxPeriodMonths) : undefined;
  const isPeriodValid = !!periodFrom && !!periodTo && periodTo >= periodFrom && (!latestPeriodTo || parseISO(periodTo) <= latestPeriodTo);
  const canSave = !isSaving && (granted === false || (granted === true && isPeriodValid));

  async function save() {
    setIsSaving(true);
    try {
      await onSave(granted ? { granted: true, periodFrom: periodFrom!, periodTo: periodTo! } : { granted: false });
    } finally {
      setIsSaving(false);
    }
  }

  const summary = [
    request.authorizationType ? authorizationTypeShortLabels[request.authorizationType] : undefined,
    request.requestedPeriodFrom && request.requestedPeriodTo ? `ønsket periode ${formatDate(request.requestedPeriodFrom)} - ${formatDate(request.requestedPeriodTo)}` : undefined,
    request.serviceProviderUnitName,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isFromAnotherKommune ? 'Registrér afgørelse' : 'Træf afgørelse'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {summary}
        </Typography>
        <RadioGroup row value={granted === undefined ? '' : String(granted)} onChange={event => setGranted(event.target.value === 'true')}>
          <FormControlLabel value="true" control={<Radio />} label={isFromAnotherKommune ? 'Godkendt' : 'Godkend'} />
          <FormControlLabel value="false" control={<Radio />} label={isFromAnotherKommune ? 'Afslået' : 'Afslå'} />
        </RadioGroup>
        {granted && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <DatePicker label="Fra dato" value={periodFrom} onChange={value => setPeriodFrom(asPlainDate(value))} variant="outlined" />
              <DatePicker label="Til dato" value={periodTo} maxDate={latestPeriodTo} onChange={value => setPeriodTo(asPlainDate(value))} variant="outlined" />
            </Box>
            {request.maxPeriodMonths && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Afgørelsen kan højst gælde i {request.maxPeriodMonths} måneder.
              </Typography>
            )}
            {request.decisionMaker && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Truffet af: {authorizationDecisionMakerLabels[request.decisionMaker]}
              </Typography>
            )}
          </>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {isFromAnotherKommune
            ? 'Registrér handlekommunens afgørelse ud fra afgørelsesbrevet. Den kan ikke ændres bagefter.'
            : 'Afgørelsen kan ikke ændres bagefter. Husk at sende afgørelsesbrevet til borgeren.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annullér</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          Gem afgørelse
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sent as a plain date so the day does not shift with the time zone.
function asPlainDate(value: DatePickerValue): string | undefined {
  return value ? formatDate(value, 'yyyy-MM-dd') : undefined;
}
